'use client';

import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useLoyaltyApi } from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import type { ReactNode } from 'react';
import { useMemo, useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import useSWR from 'swr';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { logger } from '@packages/shared/src/utils/logger/logger';
import type { PaybackClient } from './PaybackClientContext';
import { PaybackClientContext } from './PaybackClientContext';
import type { DialogState } from './types';

const messages = defineMessages({
  PaybackHeadline: { defaultMessage: 'Mein PAYBACK', id: 'Payback.Headline' },
  DialogCancelButton: { defaultMessage: 'Abbrechen', id: 'Payback.Dialog.CancelButton' },
});

declare const PB: any;

/**
 * PaybackClientProvider requires the Payback SDK to be loaded before initialization.
 *
 * Ensure the PB bootstrap script is included via <Script> tag in your server component (page.tsx):
 * ```
 * <Script src={config.order.payback.links.bootstrap[Staging|Production]} async />
 * ```
 * The SDK creates a global `PB` object that must be available before this provider renders.
 */
export const PaybackClientProvider = ({ children }: { children: ReactNode }) => {
  const [paybackClient, setPaybackClient] = useState<PaybackClient | undefined>();
  const [dialogState, setDialogState] = useState<DialogState>({
    dialogTitle: undefined,
    dialogContent: undefined,
    additionalDialogAction: undefined,
    open: false,
  });
  const loyaltyApi = useLoyaltyApi();
  const { loggedIn } = useLoginState();
  const { formatMessage, locale } = useIntl();

  useSWR(
    loyaltyApi && loggedIn ? 'loyaltySession' : null,
    () => loyaltyApi?.loyaltySession({ ecLocale: locale }),
    {
      revalidateOnFocus: false,
      onSuccess: async (data) => {
        if (data?.sessionToken) {
          if (typeof PB === 'undefined' || !PB?.mc?.core?.Bootstrap) {
            logger.error(
              'Payback SDK not loaded. Ensure PB bootstrap script is included via <Script> tag before PaybackClientProvider is rendered.',
            );
            return;
          }

          // const apiKey = await getPaybackApiKey();

          const apiKey = (await loyaltyApi?.loyaltyIframe({ ecLocale: locale }))?.iframeKey;
          PB.mc.core.Bootstrap.createClient({
            apiKey,
            token: data.sessionToken,
            onTokenRequired(resolveCallback: (token?: string) => void) {
              resolveCallback(data.sessionToken);
            },
            onTokenTooWeak() {},
            onSuccess(client: PaybackClient) {
              setPaybackClient(client);
            },
          });
        }
      },
    },
  );
  const closeDialogHandler = useCallback(() => {
    setDialogState({
      dialogTitle: undefined,
      dialogContent: undefined,
      additionalDialogAction: undefined,
      open: false,
    });
  }, []);
  const value = useMemo(
    () => ({ client: paybackClient, dialogState, setDialogState, closeDialogHandler }),
    [paybackClient, dialogState, closeDialogHandler],
  );

  return (
    <PaybackClientContext.Provider value={value}>
      {children}
      {dialogState.open && (
        <Dialog onClose={closeDialogHandler} isOpen={dialogState.open}>
          <DialogHeader onClose={closeDialogHandler}>{dialogState.dialogTitle}</DialogHeader>
          <DialogContent className="p-[2px]">{dialogState.dialogContent}</DialogContent>
          <DialogFooter>
            <Button onClick={closeDialogHandler}>
              {formatMessage(messages.DialogCancelButton)}
            </Button>
            {dialogState.additionalDialogAction}
          </DialogFooter>
        </Dialog>
      )}
    </PaybackClientContext.Provider>
  );
};
